/** A keyword's detail — `/apps/:id/keywords/:kid` (S3).
 *
 *  From 1024px up it is a drawer over the page it was opened from (오늘, 변동,
 *  later the app's keyword table); below, a page of its own. Both have the
 *  address, so a reload or a shared link lands on the keyword. Closing goes
 *  back to the page behind it, or to the app when the keyword was opened by
 *  its address.
 *
 *  It holds what the old Tracking page's Keyword Competition board and the old
 *  keyword cards kept apart: the numbers, the rank history (60 snapshots) with
 *  popularity, the top-10 board, and the two actions — analyze (calls out) and
 *  delete (asks first). */

import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api, type CompetitionApp, type CompetitionBoard, type KeywordRow } from '@/api/client'
import type { DetailState } from '@/lib/detail'
import { demandLine } from '@/lib/demand'
import { formatDay } from '@/lib/time'
import { opportunityTier } from '@/lib/opportunity'
import { invalidateKeywordData, useApp, useKeywordCompetition, useKeywordHistory, useKeywords } from '@/lib/queries'
import { difficultyBand, statusOf } from '@/lib/status'
import {
  Bars,
  Button,
  ConfirmDialog,
  CountryTag,
  DataTable,
  Delta,
  Drawer,
  EmptyState,
  ErrorState,
  errorText,
  KeyValue,
  PageHeader,
  Segmented,
  SkeletonRows,
  Sparkline,
  TierPill,
  useIsWide,
  useToast,
  When,
  type Column,
  type KeyValueItem,
  type Crumb,
} from '@/ui'
import { AppWorkspace } from './AppWorkspace'

type Tab = 'history' | 'competition'

// ── route ───────────────────────────────────────────────────────────────────

/** How the detail is drawn and how it closes. With a page behind it (opened
 *  from a list), closing goes back to that page; opened by its address,
 *  closing goes to the app, and on a wide screen the app's page is drawn under
 *  the drawer. */
function useDetailMode(overlay: boolean, appId: number) {
  const wide = useIsWide()
  const navigate = useNavigate()
  const location = useLocation()
  const bg = (location.state as DetailState | null)?.background
  const backTo = bg ? `${bg.pathname}${bg.search}` : `/apps/${appId}/keywords`
  return {
    mode: (wide ? 'drawer' : 'page') as 'drawer' | 'page',
    withApp: wide && !overlay && !bg,
    backTo,
    close: () => (bg ? navigate(-1) : navigate(backTo)),
  }
}

export function KeywordRoute({ overlay = false }: { overlay?: boolean }) {
  const params = useParams()
  const appId = Number(params.id)
  const kid = Number(params.kid)
  const m = useDetailMode(overlay, appId)
  return (
    <>
      {m.withApp && <AppWorkspace tab="keywords" />}
      <KeywordDetail key={kid} appId={appId} kid={kid} mode={m.mode} onClose={m.close} backTo={m.backTo} />
    </>
  )
}

// ── the frame ───────────────────────────────────────────────────────────────

interface FrameProps {
  mode: 'drawer' | 'page'
  onClose: () => void
  backTo: string
  title: ReactNode
  /** The page form's trail; the origin page alone when not given. */
  trail?: Crumb[]
  /** The page form's last crumb (the keyword). */
  crumb?: string
  meta?: ReactNode
  actions?: ReactNode
  children: ReactNode
}

/** The page a detail returns to, named for the parent link. */
function backLabel(to: string): string {
  const path = to.split('?')[0]
  if (path === '/') return '오늘'
  if (path.startsWith('/movement')) return '변동'
  if (path.startsWith('/apps')) return '앱'
  return '오늘'
}

function DetailFrame({ mode, onClose, backTo, title, trail, crumb, meta, actions, children }: FrameProps) {
  if (mode === 'drawer') {
    return (
      <Drawer open onClose={onClose} title={title} meta={meta} footer={actions}>
        <div className="space-y-5">{children}</div>
      </Drawer>
    )
  }
  return (
    <>
      <PageHeader title={title} parent={trail ?? { to: backTo, label: backLabel(backTo) }} crumb={crumb}>
        {meta && <div className="flex flex-wrap items-center gap-2 text-meta text-muted-foreground">{meta}</div>}
      </PageHeader>
      {actions && <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">{actions}</div>}
      <div className="space-y-4">{children}</div>
    </>
  )
}

// ── the keyword ─────────────────────────────────────────────────────────────

function KeywordDetail({ appId, kid, mode, onClose, backTo }: { appId: number; kid: number; mode: 'drawer' | 'page'; onClose: () => void; backTo: string }) {
  const app = useApp(appId)
  const keywords = useKeywords(appId)
  const row = keywords.data?.items.find((k) => k.id === kid)
  const [tab, setTab] = useState<Tab>('history')
  const [confirm, setConfirm] = useState(false)
  const confirmOpen = useRef(false)
  confirmOpen.current = confirm
  const qc = useQueryClient()
  const toast = useToast()

  const analyze = useMutation({
    mutationFn: () => api.analyzeKeyword(appId, kid),
    onSuccess: (r) => {
      invalidateKeywordData(qc, appId, kid)
      toast.success(`분석했다. 인기도 ${r.popularity} · 난이도 ${r.difficulty}`)
    },
    onError: (e) => toast.error(errorText(e)),
  })
  const del = useMutation({
    mutationFn: () => api.deleteKeyword(kid),
    onSuccess: () => {
      invalidateKeywordData(qc, appId)
      toast.success('키워드를 삭제했다')
      setConfirm(false)
      onClose()
    },
    onError: (e) => toast.error(errorText(e)),
  })

  // Escape closes the confirmation first, then the drawer.
  const close = () => (confirmOpen.current ? setConfirm(false) : onClose())

  if (!row) {
    const missing = keywords.isSuccess
    return (
      <DetailFrame mode={mode} onClose={close} backTo={backTo} title={missing ? '키워드를 열지 못했다' : '불러오는 중'}>
        {keywords.error || missing ? (
          <ErrorState title={missing ? '이 키워드는 없다. 지워졌을 수 있다' : '불러오지 못했다'} error={keywords.error} />
        ) : (
          <SkeletonRows rows={6} />
        )}
      </DetailFrame>
    )
  }

  const latest = row.latest
  const pop = latest?.popularity ?? 0
  const diff = latest?.difficulty ?? 0
  const fromApp = backTo.split('?')[0].startsWith('/apps/')

  return (
    <DetailFrame
      mode={mode}
      onClose={close}
      backTo={backTo}
      title={row.term}
      trail={fromApp ? [{ to: '/apps', label: '앱' }, { to: backTo, label: app.data?.name ?? '앱' }] : undefined}
      crumb={row.term}
      meta={
        <>
          <CountryTag code={row.country} />
          {fromApp && mode === 'page' ? (
            // the trail above already links the app
            <span className="font-medium text-foreground">{app.data?.name ?? `앱 ${appId}`}</span>
          ) : app.data ? (
            <Link to={`/apps/${appId}/keywords`} className="font-medium text-foreground hover:underline">
              {app.data.name}
            </Link>
          ) : (
            <span>앱 {appId}</span>
          )}
          <TierPill tier={opportunityTier(pop, diff)} />
        </>
      }
      actions={
        <>
          <Button icon={RefreshCw} loading={analyze.isPending} onClick={() => analyze.mutate()}>
            분석
          </Button>
          <Button variant="dangerOutline" icon={Trash2} className="ml-auto" onClick={() => setConfirm(true)}>
            삭제
          </Button>
        </>
      }
    >
      <KeyValue items={keyValues(row)} />
      <Segmented
        label="보기"
        size="sm"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'history', label: '이력' },
          { value: 'competition', label: '경쟁' },
        ]}
      />
      {tab === 'history' ? <History kid={kid} /> : <Competition kid={kid} term={row.term} />}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`“${row.term}” 키워드를 삭제할까`}
        description="이 키워드의 순위 이력도 함께 지워진다."
        confirmLabel="삭제"
        confirmVariant="destructive"
        loading={del.isPending}
        onConfirm={async () => {
          try {
            await del.mutateAsync()
          } catch {
            /* the toast already says why */
          }
        }}
      />
    </DetailFrame>
  )
}

function keyValues(row: KeywordRow): KeyValueItem[] {
  const latest = row.latest
  const rank = latest?.rank ?? 0
  const pop = latest?.popularity ?? 0
  const diff = latest?.difficulty ?? 0
  const items: KeyValueItem[] = [
    ...(row.store
      ? [
          {
            label: '스토어 순위',
            value:
              row.store.rank > 0 ? (
                <span className="font-semibold tabular-nums">
                  #{row.store.rank} <span className="font-normal text-muted-foreground">{formatDay(row.store.date)}</span>
                </span>
              ) : (
                <span className="text-muted-foreground">결과 {row.store.results_count}개 안에 없다</span>
              ),
          },
        ]
      : []),
    { label: row.store ? 'API 순위' : '순위', value: rank > 0 ? <span className="tabular-nums">#{rank}</span> : <span className="text-muted-foreground">순위 밖</span> },
    { label: '인기도', value: pop > 0 ? <span className="tabular-nums">{pop}</span> : <span className="text-muted-foreground">미측정</span> },
    ...(row.demand ? [{ label: '수요', value: <span className="text-muted-foreground">{demandLine(row.demand)}</span> }] : []),
    {
      label: '난이도',
      value: diff > 0 ? (
        <span className="tabular-nums">
          {diff} <span className="text-muted-foreground">{statusOf('difficulty', difficultyBand(diff)).label}</span>
        </span>
      ) : (
        <span className="text-muted-foreground">미측정</span>
      ),
    },
    { label: '결과 수', value: latest ? <span className="tabular-nums">{latest.results_count.toLocaleString('ko-KR')}</span> : '—' },
    { label: '태그', value: row.tag || '—' },
    { label: '출처', value: statusOf('keywordSource', row.source).label },
    ...(row.retired_at ? [{ label: '상태', value: '스토어 문구에서 빠졌다' }] : []),
    { label: '추가일', value: <When iso={row.created_at} mode="absolute" /> },
    { label: '마지막 스냅샷', value: formatDay(latest?.snapshot_date) },
  ]
  if (row.notes) items.push({ label: '메모', value: row.notes })
  return items
}

// ── 이력 ────────────────────────────────────────────────────────────────────

function History({ kid }: { kid: number }) {
  const history = useKeywordHistory(kid, 60)
  const points = useMemo(() => [...(history.data?.items ?? [])].reverse(), [history.data])
  if (history.error) return <ErrorState title="이력을 불러오지 못했다" error={history.error} onRetry={() => history.refetch()} />
  if (history.isLoading) return <SkeletonRows rows={3} />
  if (points.length === 0) {
    return <EmptyState title="이력이 아직 없다" description="스냅샷이 돌면 순위와 인기도가 날마다 쌓인다." />
  }
  const series = points.map((p) => p.rank)
  const dates = points.map((p) => p.snapshot_date)
  const ranked = series.filter((r) => r > 0)
  const popular = points.some((p) => p.popularity > 0)
  return (
    <div className="space-y-5">
      <section aria-label="순위 이력" className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h3 className="text-table font-semibold">순위</h3>
          <p className="text-meta tabular-nums text-muted-foreground">
            {ranked.length > 0 ? `최고 #${Math.min(...ranked)} · 최저 #${Math.max(...ranked)} · ` : '순위권에 든 적 없다 · '}
            스냅샷 {points.length}회
          </p>
        </div>
        <Sparkline series={series} dates={dates} width="fill" height={88} label="순위 이력" />
        <p className="flex justify-between text-micro tabular-nums text-muted-foreground">
          <span>{formatDay(dates[0])}</span>
          <span>{formatDay(dates[dates.length - 1])}</span>
        </p>
      </section>
      <section aria-label="인기도 이력" className="space-y-2">
        <h3 className="text-table font-semibold">인기도</h3>
        {popular ? (
          <Bars
            label="인기도 이력"
            labels={dates}
            outer={points.map((p) => p.popularity)}
            height={64}
            title={(i) => `${formatDay(dates[i])} 인기도 ${points[i].popularity > 0 ? points[i].popularity : '미측정'}`}
            tick={(l) => formatDay(l, undefined, false)}
          />
        ) : (
          <p className="text-table text-muted-foreground">인기도를 아직 재지 않았다.</p>
        )}
      </section>
    </div>
  )
}

// ── 경쟁 ────────────────────────────────────────────────────────────────────

/** The top-10 apps for the keyword over the last snapshots: where each stands
 *  now, whether it moved, and its line. */
function Competition({ kid, term }: { kid: number; term: string }) {
  const board = useKeywordCompetition(kid)
  if (board.error) return <ErrorState title="경쟁 목록을 불러오지 못했다" error={board.error} onRetry={() => board.refetch()} />
  const data: CompetitionBoard | undefined = board.data
  const columns: Column<CompetitionApp>[] = [
    {
      key: 'rank',
      header: '순위',
      width: '56px',
      card: 'meta',
      className: 'font-semibold tabular-nums',
      cell: (a) => (a.current_rank > 0 ? `#${a.current_rank}` : <span className="font-normal text-muted-foreground">—</span>),
    },
    {
      key: 'app',
      header: '앱',
      card: 'title',
      cell: (a) => (
        <a
          href={`https://apps.apple.com/app/id${a.track_id}`}
          target="_blank"
          rel="noreferrer"
          className="block min-w-0 truncate font-medium hover:text-accent hover:underline"
        >
          {a.name || `app ${a.track_id}`}
        </a>
      ),
    },
    { key: 'delta', header: '변동', card: 'line', cell: (a) => <Delta status={a.status} delta={a.delta} /> },
    {
      key: 'spark',
      header: '추이',
      card: 'line',
      cell: (a) => <Sparkline series={a.series} dates={data?.dates} width={80} label={`${a.name} 순위 추이`} />,
    },
  ]
  return (
    <section aria-label="경쟁" className="space-y-2">
      <p className="text-meta text-muted-foreground">“{term}” 상위 10 앱이 최근 스냅샷에서 어떻게 움직였는지 보여 준다.</p>
      <DataTable
        label="상위 10 앱"
        columns={columns}
        rows={data?.apps}
        rowKey={(a) => a.track_id}
        loading={board.isLoading}
        empty={<EmptyState title="스냅샷 데이터가 아직 없다" description="스냅샷을 돌려 상위 10을 모으고, 나중에 다시 돌리면 움직임이 보인다." />}
      />
    </section>
  )
}
